/* eslint-disable prettier/prettier */
/* eslint-disable no-console */
import { withTheme } from '@digital-retail/falabella-ui-cross-components';
import { RRProvider } from '@digital-retail/rating-and-reviews';
import { SponsoredProvider } from '@digital-retail/sponsored-provider-library';
import { isSodimacRetail, isSodimacStandalone, isWhitelabel } from '@digital-retail/store-manager';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { OkToShopInfoHead } from '../components/AdditionalInformation/ProductInfo/OkToShopHead';
import { BazaarVoiceHead } from '../components/BazaarVoice/BazaarVoice';
import Cart from '../components/Cart/Cart';
import apiConfig from '../config/api/apiConfig';
import constants from '../config/constants';
import defaultConfig from '../config/url/default.config';
import ProductContainer from '../containers/ProductContainer/ProductContainer';
import { getFuntionAnalyticsSponsored } from '../containers/ProductContainer/utils';
import loadGoogleMapScript from '../external_dependencies/googleMaps';
import { sponsoredClickServiceByXLP } from '../services/sponsoredService';
import { withApplicationContext } from '../utils/ApplicationContext';
import { getNetworkStock } from '../utils/AvailabilityHelper';
import { BuyTogetherProvider } from '../utils/BuyTogetherContext';
import { CartProvider } from '../utils/CartContext';
import { withDigitalData } from '../utils/DigitalDataContext';
import _ from '../utils/LodashImports';
import { ProductProvider } from '../utils/ProductContext';
import { RatingsProvider } from '../utils/RatingsContext';
import { filterQueryString, navigate, redirect, serializeQuery } from '../utils/RouteHandler';
import { PDPSeo } from '../utils/SEO/PDPSeo';
import { getOpenGraphData } from '../utils/SeoUtils';
import { withUser } from '../utils/UserContext';
import { isWindow } from '../utils/deviceChecker';
import { httpService } from '../utils/httpService';
import { isEmpty } from '../utils/jsUtils';
import { checkSpecification } from '../utils/product/productUtils';
import { getProductUrlParams, getRequestUrl } from '../utils/urlHelper';

import { getSponsoredConfig } from '../utils/SponsoredHelper';

const { publicRuntimeConfig } = getConfig();

let env = publicRuntimeConfig?.env ?? 'dev';

if (env === 'uat') {
  env = 'beta';
}

const Product = ({
  productData,
  siteId,
  openGraphData,
  appCtx,
  digitalData,
  storeQueryParam,
  firstName,
  isUserLoggedIn,
  theme,
}) => {
  const { FEATURE_TOGGLE_SPONSORED_VAS_SERVICE, GOOGLE_MAP_API_KEY = '' } = publicRuntimeConfig;
  const hasRun = useRef(false);
  const { sponsoredClickService } = getFuntionAnalyticsSponsored(FEATURE_TOGGLE_SPONSORED_VAS_SERVICE);

  useEffect(() => {
    if (window && !hasRun.current) {
      hasRun.current = true;
      const currentUrl = new URL(window.location);
      const params = currentUrl.searchParams;

      const sponsoredClickDataEncoded = params.get('sponsoredClickData');

      if (sponsoredClickDataEncoded) {
        const sponsoredClickData = decodeURIComponent(sponsoredClickDataEncoded);
        const clickEventSponsoredProduct = JSON.parse(sponsoredClickData);
        params.delete('sponsoredClickData');
        window.history.replaceState(null, '', currentUrl.toString());

        const isXLPclickEvent = clickEventSponsoredProduct.isXLP;

        if (isXLPclickEvent) {
          sponsoredClickServiceByXLP(clickEventSponsoredProduct);
        } else {
          sponsoredClickService(clickEventSponsoredProduct);
        }
      }
    }
  }, []);

  digitalData.setPage(constants.PAGES.PDP);
  const environment = publicRuntimeConfig.ENVIRONMENT ?? 'production';
  const isSecondaryRatingsFormEnabled = _.get(appCtx, 'siteConfig.toggles.isSecondaryRatingsFormEnabled', true);
  const isReviewValidationWithOrder = _.get(appCtx, 'siteConfig.toggles.isReviewValidationWithOrder', true);
  const isOkToShopFeatureEnabled = _.get(appCtx, 'siteConfig.toggles.isOkToShopFeatureEnabled', false);
  const CF_GET_REVIEWS_URL = _.get(
    appCtx,
    'siteConfig.endpoints.CF_GET_REVIEWS_URL',
    'https://bvp.falabella.services/'
  );
  const bazaarVoiceScriptUrl = _.get(appCtx, 'siteConfig.endpoints.BAZAARVOICE_SCRIPT_URL', '');

  const isSodimacSA = isSodimacStandalone(appCtx.store);
  const isSodimacRE = isSodimacRetail(appCtx.store);
  const rrLabel = isSodimacSA
    ? _.get(appCtx, 'siteConfig.textDictionary.SO_COM_BV_WRITE_REVIEW', 'Evaluar')
    : _.get(appCtx, 'siteConfig.textDictionary.BV_WRITE_REVIEW', 'Calificar');
  const recommendedThreshold = _.get(appCtx, 'siteConfig.textDictionary.BV_RECOMMENDED_THRESHOLD', 80);
  useEffect(() => {
    loadGoogleMapScript(GOOGLE_MAP_API_KEY, siteId);
  }, []);

  useEffect(() => {
    if (appCtx && appCtx.busy) {
      appCtx.setBusy(false);
    }
    appCtx.attachResizeListener();
  }, [productData]);
  const gender =
    [...(productData?.attributes?.specifications || []), ...(productData?.attributes?.topSpecifications || [])].find(
      (attribute) => attribute.name === constants.GENDER_ATTRIBUTE
    )?.value ?? undefined;

  const configValue = getSponsoredConfig(productData, appCtx, publicRuntimeConfig, gender);
  const enableEnglishReviews = _.get(appCtx, 'siteConfig.toggles.enableEnglishReviews', false);
  return (
    <ProductProvider data={productData}>
      <SponsoredProvider config={configValue}>
        <CartProvider>
          <RatingsProvider>
            <RRProvider
              product={productData}
              firstName={firstName}
              label={rrLabel}
              isLoggedIn={isUserLoggedIn}
              isSecondaryRatingsFormEnabled={isSecondaryRatingsFormEnabled}
              isReviewValidationWithOrder={isReviewValidationWithOrder}
              tenant={appCtx.regionCode}
              environment={environment}
              theme={theme}
              recommendedThreshold={recommendedThreshold}
              isSodimacStandAlone={isSodimacSA || isSodimacRE}
              fetchApiBaseUrl={CF_GET_REVIEWS_URL}
              enableEnglishReviews={enableEnglishReviews}
            >
              <BuyTogetherProvider>
                <PDPSeo
                  siteProps={{
                    regionCode: siteId,
                    product: {
                      brandName: productData?.brandName,
                      name: productData?.name,
                      slug: productData?.slug,
                      variantId: productData?.currentVariant,
                      productId: productData?.id,
                      isOutOfStock: productData?.isOutOfStock,
                      description: productData?.description,
                      sellerName: _.get(productData, 'variants[0].offerings[0].sellerName', ''),
                    },
                    openGraphData,
                    variants: productData?.variants,
                    breadcrumb: productData?.breadCrumb,
                    appCtx,
                    store: storeQueryParam,
                  }}
                />
                {isOkToShopFeatureEnabled && <OkToShopInfoHead />}
                <BazaarVoiceHead url={bazaarVoiceScriptUrl} />
                <Cart />
                <ProductContainer storeQueryParam={storeQueryParam} />
              </BuyTogetherProvider>
            </RRProvider>
          </RatingsProvider>
        </CartProvider>
      </SponsoredProvider>
    </ProductProvider>
  );
};

const shouldRedirect = (responseType) => {
  return responseType === constants.SEARCH_REDIRECT_STATUS;
};

const redirectNoProductResult = (response, site) => {
  if (isWindow()) {
    navigate({
      url: '/notFound',
      site,
    });
    return;
  }

  if (response) {
    const url = `/${site}/notFound`;
    response.writeHead(constants.REDIRECT_CODE, { Location: url });
    response.end();
  }
};

const redirectProduct = (response, url) => {
  if (isWindow()) {
    redirect(url, true);
    return;
  }

  if (response) {
    response.writeHead(constants.HTTP_RES_CODE.PERM_REDIRECT, {
      Location: url,
    });
    response.end();
  }
};

const isCanonicalUrl = (currentUrl, productData) => {
  const { slug } = productData;
  const urlPath = currentUrl.match('(/{0,1}[^?#]*)')[0].split('/');
  if (!urlPath[4]) {
    return true;
  }
  const currentName = decodeURIComponent(urlPath[4]);
  return currentName === slug;
};

const shouldSEORedirect = (appCtx, currentUrl, productData) => {
  // Need to remove this once we get confirmation from SEO team
  const isRedirect301EnabledForPDP = _.get(appCtx, 'siteConfig.toggles.isRedirect301EnabledForPDP');
  return isRedirect301EnabledForPDP && !isCanonicalUrl(currentUrl, productData);
};

const getRedirectToFcomUrl = ({ product, envPath, region, params }) => {
  const paramString = serializeQuery(params);
  const regionGeoDomain = region === 'cl' ? '' : `.${region}`;
  return `https://${envPath}.falabella.com${regionGeoDomain}/falabella-${region}/product/${product.id}/${
    product.slug
  }/${product.currentVariant}${paramString ? `?${paramString}` : ''}`;
};

const getRedirectToSubdomain = ({ product, envPath, region, subDomain, params = '' }) => {
  const paramString = serializeQuery(params);
  const topLevelDomain = region === 'cl' ? 'com' : `com.${region}`;
  return `https://${envPath}${subDomain}.falabella.${topLevelDomain}/${subDomain}-${region}/product/${product.id}/${
    product.slug
  }/${product.currentVariant}${paramString ? `?${paramString}` : ''}`;
};

const getRedirectToNotFoundUrl = ({ storeKey, envPath, region, subDomain }) => {
  const topLevelDomain = region === 'cl' ? region : `com.${region}`;
  const topLevelDomainForMKP = region === 'cl' ? 'com' : `com.${region}`;
  if (storeKey === 'marketplace') {
    return `https://${envPath}.falabella.${topLevelDomainForMKP}/falabella-${region}/notFound`;
  }
  return `https://${envPath}.${subDomain}.${topLevelDomain}/${subDomain}-${region}/notFound`;
};

const redirectToWhitelabelNotFoundUrl = ({ storeKey, envPath, region, subDomain }) => {
  const regionSuffix = region === 'cl' ? 'com' : `com.${region}`;
  const whitelabelDomain = subDomain || constants.whitelabels.find((item) => item.exp === storeKey)?.domain;

  return `https://${envPath}${whitelabelDomain}.falabella.${regionSuffix}/${whitelabelDomain}-${region}/notFound`;
};

const getReloadUrl = ({ params = {}, tenant = '', pathname = '' }) => {
  const paramString = serializeQuery(params, ['exp']);
  const newUrl = `/${tenant}${pathname}${paramString ? `?${paramString}` : ''}`;
  return newUrl;
};

function isValidProductSite({ CONFIGS_PER_STORE, storeKey, productSites }) {
  const { validProductSites } = CONFIGS_PER_STORE[storeKey];
  return productSites.some((productSite) => validProductSites.includes(productSite));
}

const checkMatchSellerRegex = ({ sellerName, sellerRedirections }) => {
  return sellerRedirections.find((redirection) => redirection.regex.test(sellerName?.toUpperCase()));
};

const getRedirectUrl = ({ ctx, product, CONFIGS_PER_STORE, base = '' }) => {
  const { productSites = [] } = product;
  const { store, regionCode, subdomain, url, tenant } = ctx;
  const filteredParams = filterQueryString(url, constants.ALLOWED_PARAMS_REDIRECT);

  const storeKey = store ? store.toLowerCase() : 'marketplace';
  const thereIsMatch = isValidProductSite({
    CONFIGS_PER_STORE,
    storeKey,
    productSites,
  });
  const envPath = env;
  const sisEnvPath = env === 'www' ? '' : `${env}-`;

  try {
    const urlBase = new URL(base);
    const currentUrl = new URL(url, urlBase.origin);
    const validTenant = ['sodimac-cl', 'sodimac-pe'];

    if (currentUrl.searchParams.has('exp') && validTenant.includes(tenant)) {
      return getReloadUrl({
        params: filteredParams,
        pathname: currentUrl.pathname,
        tenant,
      });
    }
  } catch (error) {
    console.error('Error in getReloadUrl', error);
  }

  if (!thereIsMatch && !isSodimacRetail(store)) {
    if (CONFIGS_PER_STORE[storeKey].redirectsToFallback) {
      return getRedirectToFcomUrl({
        product,
        envPath,
        region: regionCode,
        params: filteredParams,
      });
    }
    if (isWhitelabel({ store: storeKey })) {
      return redirectToWhitelabelNotFoundUrl({
        storeKey,
        envPath: sisEnvPath,
        region: regionCode,
        subDomain: subdomain,
      });
    }
    return getRedirectToNotFoundUrl({
      storeKey,
      envPath,
      region: regionCode,
      subDomain: subdomain,
    });
  }
  // handle if a redirection to SIS is needed
  if (CONFIGS_PER_STORE[storeKey]?.sellerRedirections?.length) {
    const sellerMatch = checkMatchSellerRegex({
      sellerName: product.sellerInfo?.sellerName,
      sellerRedirections: CONFIGS_PER_STORE[storeKey]?.sellerRedirections,
    });
    if (
      sellerMatch &&
      !isEmpty(sellerMatch) &&
      process.env.NODE_ENV !== 'development' &&
      subdomain !== sellerMatch.forceSubdomain
    ) {
      return getRedirectToSubdomain({
        product,
        envPath: sisEnvPath,
        region: regionCode,
        subDomain: sellerMatch.forceSubdomain,
        params: filteredParams,
      });
    }
  }

  return '';
};

Product.getInitialProps = async ({ config }, { query, req, res, asPath }, appCtx) => {
  const { productName, site, subdomain, store: storeQuery, ...params } = query;
  const store = storeQuery || subdomain || appCtx.store;
  const siteId = `${query.site.substr(query.site.indexOf('-') + 1, 2)}`;

  const updatedParams = getProductUrlParams({ params, appCtx, store });

  const getProductDetailsUrl = apiConfig.getProductDetailsUrl({
    base: isWindow() ? config.API_HOST : config.CLUSTER_API_HOST,
    siteId,
    params: updatedParams,
  });

  const response = await httpService(appCtx).get(getProductDetailsUrl);
  const { data, error } = response;

  if (error) {
    throw new Error(constants.ERROR_MESSAGE_TEXT);
  }
  const productData = data.data;
  productData.stockUnits = {};
  const isPaintCanWithoutColor = checkSpecification({
    product: productData,
    specId: 'Color',
    specValue: 'Sin Color',
  });

  if (isPaintCanWithoutColor) {
    redirectNoProductResult(res, query.site);
  }

  if ('variants' in productData) {
    productData.variants.forEach((v) => {
      productData.stockUnits[v.id] = getNetworkStock(v.networkStock);
    });
  }

  if (productData.linkedProducts) {
    Object.values(productData.linkedProducts).forEach((linkedProducts) => {
      linkedProducts.variants.forEach((v) => {
        productData.stockUnits[v.id] = getNetworkStock(v.networkStock);
      });
    });
  }

  const isNotFound = data.responseType === constants.NOT_FOUND;
  if (isNotFound) {
    redirectNoProductResult(res, query.site);
    return undefined;
  }
  // return the product JSON with out-of-stock state (for conditional display in UI)
  productData.isOutOfStock = data.responseType === constants.OUT_OF_STOCK;
  if (shouldRedirect(data.responseType)) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    redirectProduct(res, data.data.altUrl);
    return undefined;
  }
  const openGraphData = getOpenGraphData(productData, appCtx, store, 'PDP', config);
  const currentUrl = isWindow() ? asPath : getRequestUrl(req.url, appCtx.tenant);

  if (shouldSEORedirect(appCtx, currentUrl, productData)) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    const urlToRedirect = encodeURI(openGraphData.canonicalUrl);

    redirectProduct(res, urlToRedirect);
    return undefined;
  }
  const urlToRedirect = getRedirectUrl({
    ctx: appCtx,
    product: productData,
    CONFIGS_PER_STORE: defaultConfig.CONFIGS_PER_STORE,
    base: isWindow() ? config.API_HOST : config.CLUSTER_API_HOST,
  });
  if (urlToRedirect) {
    redirectProduct(res, urlToRedirect);
    return undefined;
  }

  const disableQuantitySelection = _.get(appCtx, 'siteConfig.toggles.disableQuantitySelection', false);

  productData.disableQuantitySelection = disableQuantitySelection;

  return { productData, siteId, openGraphData, storeQueryParam: storeQuery };
};

Product.propTypes = {
  productData: PropTypes.object.isRequired,
  openGraphData: PropTypes.object.isRequired,
  siteId: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  digitalData: PropTypes.object.isRequired,
  storeQueryParam: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  theme: PropTypes.object.isRequired,
};

export {
  getRedirectToFcomUrl,
  getRedirectToNotFoundUrl,
  getRedirectUrl,
  isCanonicalUrl,
  isValidProductSite,
  redirectNoProductResult,
  redirectProduct,
  shouldRedirect,
  shouldSEORedirect
};
export default withUser(withApplicationContext(withDigitalData(withTheme(Product))));
